import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Verification from './pages/Verification';
import Profile from './pages/Profile';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminTranslations from './pages/AdminTranslations';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import { useLanguage } from './LanguageContext';
import { translateError } from './i18n/translate';
import './App.css';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API base URL
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : 'http://localhost:3004';

// De-duplicate concurrent admin refreshes: when several admin requests 401 at
// once, they all await the same in-flight refresh instead of firing N calls.
let adminRefreshPromise = null;

// Exchange the stored admin refresh token for a fresh access token. Returns the
// new access token, or throws if no refresh token is stored or the refresh
// itself is rejected (dead session).
const refreshAdminToken = () => {
  if (adminRefreshPromise) return adminRefreshPromise;

  const storedRefreshToken = localStorage.getItem('admin_refresh_token');
  if (!storedRefreshToken) return Promise.reject(new Error('No admin refresh token'));

  adminRefreshPromise = fetch(`${API_URL}/api/admin/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: storedRefreshToken })
  })
    .then(async (res) => {
      if (!res.ok) throw new Error('Admin refresh failed');
      const data = await res.json();
      localStorage.setItem('admin_token', data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem('admin_refresh_token', data.refreshToken);
      }
      return data.accessToken;
    })
    .finally(() => {
      adminRefreshPromise = null;
    });

  return adminRefreshPromise;
};

function App() {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const adminToken = localStorage.getItem('admin_token');

    const checkAuth = async () => {
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/auth/verify?token=${token}`);
          const data = await res.json();
          if (data.valid) {
            setUser(data.customer);
          } else {
            localStorage.removeItem('auth_token');
          }
        } catch (err) {
          console.error('Auth check failed:', err);
        }
      }

      if (adminToken) {
        // Admin tokens are handled by Supabase
        setAdmin({ token: adminToken });
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(translateError(data, t));
    }

    localStorage.setItem('auth_token', data.token);
    setUser(data.customer);
    return data;
  };

  // Register function
  const register = async (formData) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(translateError(data, t));
    }

    localStorage.setItem('auth_token', data.token);
    setUser(data.customer);
    return data;
  };

  // Forgot password: request a reset email
  const forgotPassword = async (email) => {
    const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(translateError(data, t));
    }

    return data;
  };

  // Reset password using an emailed token
  const resetPassword = async (token, newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword })
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(translateError(data, t));
    }

    return data;
  };

  // Logout function
  const logout = async () => {
    const token = localStorage.getItem('auth_token');
    
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'X-Session-Token': token }
        });
      } catch (err) {
        console.error('Logout error:', err);
      }
    }

    localStorage.removeItem('auth_token');
    setUser(null);
  };

  // Admin login
  const adminLogin = async (email, password) => {
    const res = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || 'Admin login failed');
    }

    localStorage.setItem('admin_token', data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem('admin_refresh_token', data.refreshToken);
    } else {
      localStorage.removeItem('admin_refresh_token');
    }
    setAdmin(data.admin);
    return data;
  };

  // Admin logout
  const adminLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    setAdmin(null);
  };

  // Get authenticated fetch helper
  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      headers['X-Session-Token'] = token;
    }

    return fetch(`${API_URL}${url}`, { ...options, headers });
  };

  // Admin fetch helper. Transparently refreshes an expired admin access token
  // and retries once on 401. If the refresh token is missing/expired, the
  // session is dead: clear it so the user is redirected to /admin/login instead
  // of staring at a misleading empty dashboard.
  const adminFetch = async (url, options = {}) => {
    const doFetch = (token) => {
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      return fetch(`${API_URL}${url}`, { ...options, headers });
    };

    let res = await doFetch(localStorage.getItem('admin_token'));

    if (res.status === 401) {
      try {
        const newToken = await refreshAdminToken();
        res = await doFetch(newToken);
      } catch (err) {
        // No refresh token or the refresh was rejected — session is dead.
        adminLogout();
      }
    }

    return res;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user,
      admin,
      login,
      register,
      forgotPassword,
      resetPassword,
      logout,
      adminLogin,
      adminLogout,
      authFetch,
      adminFetch
    }}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          user ? <Navigate to="/dashboard" /> : <Login />
        } />
        <Route path="/register" element={
          user ? <Navigate to="/dashboard" /> : <Register />
        } />
        <Route path="/forgot-password" element={
          user ? <Navigate to="/dashboard" /> : <ForgotPassword />
        } />
        <Route path="/reset-password" element={
          user ? <Navigate to="/dashboard" /> : <ResetPassword />
        } />

        {/* Protected Customer Routes */}
        <Route path="/" element={
          user ? <Layout /> : <Navigate to="/login" />
        }>
          <Route index element={<Navigate to="/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="verification" element={<Verification />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Admin Routes */}
        <Route path="/admin/login" element={
          admin ? <Navigate to="/admin" /> : <AdminLogin />
        } />
        <Route path="/admin" element={
          admin ? <AdminLayout /> : <Navigate to="/admin/login" />
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="translations" element={<AdminTranslations />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </AuthContext.Provider>
  );
}

export default App;

